// 계좌 관리 화면 (tb_accounts)
// 좌측: 계좌 목록 그리드 / 우측: 등록·수정 폼
import React, { useState, useEffect, useCallback } from 'react';
import { fetchAccounts, createAccount, updateAccount, deleteAccount } from '../../api/accounts';
import PageLayout from '../../components/PageLayout';
import './AccountPage.css';

const EMPTY_FORM = { account_name: '', account_desc: '', sort_order: 0 };

const formatDate = (dateStr) =>
  dateStr ? new Date(dateStr).toLocaleDateString('ko-KR') : '';

const AccountPage = () => {
  const [accounts,  setAccounts]  = useState([]);
  const [form,      setForm]      = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [message,   setMessage]   = useState({ text: '', type: '' });

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  const loadAccounts = useCallback(async () => {
    try {
      const data = await fetchAccounts();
      setAccounts(data);
    } catch {
      showMessage('목록 조회에 실패했습니다.', 'error');
    }
  }, []);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  const handleRowClick = (account) => {
    setEditingId(account.id);
    setForm({ account_name: account.account_name, account_desc: account.account_desc || '', sort_order: account.sort_order ?? 0 });
  };

  const handleNew = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleFormChange = (e) => {
    const { name, value, type } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'number' ? Number(value) : value }));
  };

  const handleSave = async () => {
    if (!form.account_name.trim()) {
      showMessage('계좌명을 입력해주세요.', 'error');
      return;
    }
    setLoading(true);
    try {
      if (editingId === null) {
        await createAccount(form);
        showMessage('계좌가 등록되었습니다.');
      } else {
        await updateAccount(editingId, form);
        showMessage('계좌가 수정되었습니다.');
      }
      await loadAccounts();
      handleNew();
    } catch (err) {
      showMessage(err.response?.data?.detail || '저장에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, accountName, e) => {
    e.stopPropagation();
    if (!window.confirm(`"${accountName}" 계좌를 삭제하시겠습니까?`)) return;
    setLoading(true);
    try {
      await deleteAccount(id);
      showMessage('계좌가 삭제되었습니다.');
      await loadAccounts();
      if (editingId === id) handleNew();
    } catch (err) {
      showMessage(err.response?.data?.detail || '삭제에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const toolbar = (
    <button className="btn btn-primary" onClick={handleNew}>+ 신규</button>
  );

  return (
    <PageLayout title="계좌 관리" toolbar={toolbar} message={message}>
      <div className="account-body">

        {/* ── 좌측: 계좌 목록 그리드 ── */}
        <div className="account-list-panel">
          <div className="grid-wrapper">
            <table className="grid-table">
              <thead>
                <tr>
                  <th style={{ width: 160 }}>계좌명</th>
                  <th className="col-center" style={{ width: 50 }}>정렬</th>
                  <th>설명</th>
                  <th className="col-center" style={{ width: 110 }}>등록일</th>
                  <th className="col-center" style={{ width: 60 }}>삭제</th>
                </tr>
              </thead>
              <tbody>
                {accounts.length === 0 ? (
                  <tr><td colSpan={5} className="grid-empty">데이터가 없습니다.</td></tr>
                ) : (
                  accounts.map((account) => (
                    <tr
                      key={account.id}
                      onClick={() => handleRowClick(account)}
                      className={editingId === account.id ? 'selected' : ''}
                    >
                      <td>{account.account_name}</td>
                      <td className="col-center">{account.sort_order}</td>
                      <td className="col-desc">{account.account_desc || ''}</td>
                      <td className="col-center">{formatDate(account.created_at)}</td>
                      <td className="col-center">
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={(e) => handleDelete(account.id, account.account_name, e)}
                          disabled={loading}
                        >
                          삭제
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── 우측: 등록·수정 폼 ── */}
        <div className="account-form-panel">
          <h3 className="form-title">{editingId === null ? '신규 등록' : '계좌 수정'}</h3>

          <div className="form-group">
            <label>계좌명 <span className="required">*</span></label>
            <input
              type="text"
              name="account_name"
              value={form.account_name}
              onChange={handleFormChange}
              placeholder="예) IRP, ISA, 위탁"
              maxLength={100}
            />
          </div>

          <div className="form-group">
            <label>정렬순서</label>
            <input
              type="number"
              name="sort_order"
              value={form.sort_order}
              onChange={handleFormChange}
              min={0}
              max={9999}
            />
          </div>

          <div className="form-group">
            <label>설명</label>
            <textarea
              name="account_desc"
              value={form.account_desc}
              onChange={handleFormChange}
              placeholder="계좌 설명 (선택)"
              maxLength={500}
              rows={4}
            />
          </div>

          <div className="form-actions">
            <button className="btn btn-primary" onClick={handleSave} disabled={loading}>
              {loading ? '저장 중...' : '저장'}
            </button>
            <button className="btn btn-secondary" onClick={handleNew} disabled={loading}>취소</button>
          </div>
        </div>

      </div>
    </PageLayout>
  );
};

export default AccountPage;
